import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import rg4js from "raygun4js";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  CommunicationLanguage,
  CommunicationStatus,
  CommunicationType,
  ConsumerTaskStatus,
  ContactLanguages,
  ObjectTypeEnum,
} from "@/briefpoint-client";
import { toast } from "@/components/ui/toast";
import {
  clientGetOptions,
  communicationGetBulkTranslationTaskOptions,
  communicationGetSimplifyAllTaskByCommunicationOptions,
  communicationGetSimplifyAllTaskOptions,
  getCommunicationsForObjectOptions,
} from "@/lib/query-options";
import { normalizeError } from "@/lib/utils";
import type { ReviewClientTasksPageData } from "./useReviewClientTasksPageData";
import {
  type BulkSimplifyActionSource,
  type ClientContactLanguageParams,
  clearSimplifyAllTaskIdFromSession,
  contactLanguageToCommunicationLanguage,
  readSimplifyAllTaskIdFromSession,
  writeSimplifyAllTaskIdToSession,
} from "./utils";

/**
 * Simplify-all / verbatim batch, session resume, and client language + bulk
 * Spanish translation. Kept in one hook: mutations reference each other (e.g.
 * startSimplifyAll resets startBulkTranslation).
 */
export function useReviewClientTasksSimplifyAndLanguage(
  pageData: ReviewClientTasksPageData,
) {
  const documentId = pageData.documentId;
  const user = pageData.user;
  const documentQuery = pageData.documentQuery;
  const caseQuery = pageData.caseQuery;
  const clientQuery = pageData.clientQuery;
  const communicationApi = pageData.communicationApi;
  const clientApi = pageData.clientApi;
  const communicationsQuery = pageData.communicationsQuery;
  const activeCommunication = pageData.activeCommunication;
  const queryClient = useQueryClient();
  const isReviewLocked = pageData.isReviewLocked;
  const primaryTaskCount = pageData.primaryTaskCount;
  const hasClientTasks = pageData.hasClientTasks;
  const isEmptyClientTasksState = pageData.isEmptyClientTasksState;
  const requestName = pageData.requestName;
  const [language, setLanguage] = useState<CommunicationLanguage>(
    CommunicationLanguage.EN,
  );
  const lastSyncedContactLanguage = useRef<ContactLanguages | null>(null);
  const bulkTranslationComplete = useRef(false);
  const prevNeedsBulkSpanishTranslationRef = useRef(false);

  // Create Client Tasks / Simplify All Workflow
  const simplifyAllWorkflowComplete = useRef(false);
  const simplifyAllSawActiveWorkRef = useRef(false);
  const prevIsBulkSimplifyJobBusy = useRef(false);
  const [bulkSimplifyLoadingSource, setBulkSimplifyLoadingSource] =
    useState<BulkSimplifyActionSource | null>(null);
  // after bulk simplify (create tasks) finishes, do not start translate-task until comms data has refetched
  const [
    awaitingCommsAfterBulkSimplifySuccess,
    setAwaitingCommsAfterBulkSimplifySuccess,
  ] = useState(false);
  // verbatim "create task for all" — show creating copy until comms + tasks are actually on screen
  const [
    isVerbatimAllAwaitingTasksOnScreen,
    setIsVerbatimAllAwaitingTasksOnScreen,
  ] = useState(false);
  // after bulk simplify: keep progress + hide task list until comms refetch returns (stale otherwise shows only pre-existing items)
  const [isAwaitingCommsAfterSimplifyAll, setIsAwaitingCommsAfterSimplifyAll] =
    useState(false);

  // read session synchronously on each render so we never fire GET by communication for resume before we know a stashed task id (avoids 404 on reload while simplify-all is running)
  const simplifyAllTaskIdFromSession =
    documentId && activeCommunication?.id
      ? (readSimplifyAllTaskIdFromSession(documentId, activeCommunication.id) ??
        undefined)
      : undefined;
  useLayoutEffect(() => {
    if (simplifyAllTaskIdFromSession) {
      simplifyAllSawActiveWorkRef.current = true;
    }
  }, [simplifyAllTaskIdFromSession]);

  // Sync the client's contact language with the language control
  useEffect(() => {
    if (!clientQuery.isSuccess || !clientQuery.data) return;
    const resolvedContactLanguage =
      clientQuery.data.contactLanguage ?? ContactLanguages.EnglishUs;
    if (
      lastSyncedContactLanguage.current !== null &&
      resolvedContactLanguage === lastSyncedContactLanguage.current
    ) {
      return;
    }
    lastSyncedContactLanguage.current = resolvedContactLanguage;
    setLanguage(
      contactLanguageToCommunicationLanguage(resolvedContactLanguage),
    );
  }, [
    clientQuery.isSuccess,
    clientQuery.data,
    clientQuery.data?.contactLanguage,
  ]);

  useEffect(() => {
    if (hasClientTasks) {
      setIsVerbatimAllAwaitingTasksOnScreen(false);
    }
  }, [hasClientTasks]);

  // reset when switching documents
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally only documentId
  useEffect(() => {
    setIsVerbatimAllAwaitingTasksOnScreen(false);
    setIsAwaitingCommsAfterSimplifyAll(false);
  }, [documentId]);

  const isBulkSpanishTranslationNeeded = useMemo(() => {
    if (language !== CommunicationLanguage.ES) return false;
    const items = activeCommunication?.externalCommunicationItems ?? [];
    const assignedEnglishPrimaries = items.filter(
      (c) =>
        (c.responseInfo?.associatedObjectIds?.length ?? 0) > 0 &&
        !c.translatedExternalCommunicationItemId,
    );
    return assignedEnglishPrimaries.some((primary) => {
      if (!primary.id) return false;
      return !items.some(
        (c) => c.translatedExternalCommunicationItemId === primary.id,
      );
    });
  }, [language, activeCommunication?.externalCommunicationItems]);

  useEffect(() => {
    if (
      isBulkSpanishTranslationNeeded &&
      !prevNeedsBulkSpanishTranslationRef.current
    ) {
      bulkTranslationComplete.current = false;
    }
    prevNeedsBulkSpanishTranslationRef.current = isBulkSpanishTranslationNeeded;
  }, [isBulkSpanishTranslationNeeded]);

  const startBulkTranslationMutation = useMutation({
    mutationFn: async () => {
      return communicationApi.communicationStartBulkTranslation({
        id: activeCommunication?.id ?? "",
        toLang: CommunicationLanguage.ES,
      });
    },
    onMutate: () => {
      bulkTranslationComplete.current = false;
    },
    onError: (error) => {
      toast.error("Failed to start bulk translation. Please try again.");
      rg4js("send", {
        error: normalizeError(error),
      });
    },
  });

  const translationPollingQuery = useQuery({
    ...communicationGetBulkTranslationTaskOptions(
      communicationApi,
      startBulkTranslationMutation.data?.id,
    ),
    refetchInterval: (query) => {
      //TODO: change this out when the backend is updated to use the correct status type
      if (query.state.data?.status === ConsumerTaskStatus.Complete) {
        return false;
      }
      return 1500;
    },
    refetchIntervalInBackground: true,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });

  const isBulkTranslationWorkInProgress = useMemo(() => {
    const pollStatus = translationPollingQuery.data?.status;
    const isBulkTranslationPollInFlight =
      !!startBulkTranslationMutation.data?.id &&
      pollStatus !== ConsumerTaskStatus.Complete &&
      pollStatus !== ConsumerTaskStatus.Error;
    return (
      startBulkTranslationMutation.isPending || isBulkTranslationPollInFlight
    );
  }, [
    startBulkTranslationMutation.isPending,
    startBulkTranslationMutation.data?.id,
    translationPollingQuery.data?.status,
  ]);

  const createDraftClientCommunicationMutation = useMutation({
    mutationFn: async (): Promise<string> => {
      const doc = documentQuery.data;
      const caseData = caseQuery.data;
      if (!doc?.caseId || !caseData?.shortTitle) {
        throw new Error("Case data not available");
      }
      const newCommunication =
        await communicationApi.communicationCreateClientCommunication({
          createClientCommunication: {
            objectType: ObjectTypeEnum.Document,
            objectId: documentId,
            caseId: doc.caseId,
            communicationType: CommunicationType.Review,
            status: CommunicationStatus.Pending,
            communicationInfo: {
              title: requestName,
              caseTitle: caseData.title || caseData.shortTitle,
            },
          },
        });
      const newId = newCommunication.id;
      if (!newId) {
        throw new Error("Communication id missing");
      }
      return newId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getCommunicationsForObjectOptions(
          communicationApi,
          documentId,
          ObjectTypeEnum.Document,
        ).queryKey,
      });
    },
    onError: (error) => {
      toast.error("Failed to create client communication. Please try again.");
      rg4js("send", {
        error: normalizeError(error),
      });
    },
  });

  const startSimplifyAllMutation = useMutation({
    mutationFn: async (communicationId: string) => {
      return communicationApi.communicationStartSimplifyAllTask({
        startSimplifyAllTaskRequest: {
          externalCommunicationId: communicationId,
        },
      });
    },
    onMutate: () => {
      simplifyAllWorkflowComplete.current = false;
      simplifyAllSawActiveWorkRef.current = false;
      bulkTranslationComplete.current = false;
      startBulkTranslationMutation.reset();
    },
    onSuccess: (data, externalCommunicationId) => {
      if (!data.id || !documentId) {
        return;
      }
      writeSimplifyAllTaskIdToSession(
        documentId,
        externalCommunicationId,
        data.id,
      );
      simplifyAllSawActiveWorkRef.current = true;
    },
    onError: (error, externalCommunicationId) => {
      if (documentId && externalCommunicationId) {
        clearSimplifyAllTaskIdFromSession(documentId, externalCommunicationId);
      }
      toast.error("Failed to start bulk client tasks. Please try again.");
      rg4js("send", {
        error: normalizeError(error),
      });
    },
  });

  const batchCreateTasksForAllMutation = useMutation({
    mutationFn: async (communicationId: string) => {
      return communicationApi.communicationBatchCreateTasks({
        id: communicationId,
      });
    },
    onMutate: () => {
      bulkTranslationComplete.current = false;
      startBulkTranslationMutation.reset();
    },
    onSuccess: () => {
      if (language === CommunicationLanguage.ES) {
        setAwaitingCommsAfterBulkSimplifySuccess(true);
      }
      queryClient.invalidateQueries({
        queryKey: getCommunicationsForObjectOptions(
          communicationApi,
          documentId,
          ObjectTypeEnum.Document,
        ).queryKey,
      });
    },
    onError: (error) => {
      setIsVerbatimAllAwaitingTasksOnScreen(false);
      toast.error("Failed to create client tasks. Please try again.");
      rg4js("send", {
        error: normalizeError(error),
      });
    },
  });

  // allows checking if the simplify all task is already in progress by communication id (only when we do not already have a task id to poll, and not while other bulk work would make the endpoint 404)
  const isSimplifyAllGetByCommunicationSkipped =
    !!startSimplifyAllMutation.data?.id ||
    !!simplifyAllTaskIdFromSession ||
    isBulkTranslationWorkInProgress ||
    createDraftClientCommunicationMutation.isPending ||
    batchCreateTasksForAllMutation.isPending;
  const simplifyAllByCommunicationQuery = useQuery({
    ...communicationGetSimplifyAllTaskByCommunicationOptions(
      communicationApi,
      activeCommunication?.id,
    ),
    enabled:
      !!activeCommunication?.id && !isSimplifyAllGetByCommunicationSkipped,
  });

  // get the simplify all task id from the resume response
  const simplifyAllTaskIdFromResume = useMemo(() => {
    const t = simplifyAllByCommunicationQuery.data;
    if (t == null) {
      return undefined;
    }
    if (!t.id) {
      return undefined;
    }
    if (
      t.status === ConsumerTaskStatus.Complete ||
      t.status === ConsumerTaskStatus.Error
    ) {
      return undefined;
    }
    return t.id;
  }, [simplifyAllByCommunicationQuery.data]);

  const simplifyAllTaskId =
    startSimplifyAllMutation.data?.id ??
    simplifyAllTaskIdFromResume ??
    simplifyAllTaskIdFromSession;

  const simplifyAllPollingQuery = useQuery({
    ...communicationGetSimplifyAllTaskOptions(
      communicationApi,
      simplifyAllTaskId,
    ),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (
        status === ConsumerTaskStatus.Complete ||
        status === ConsumerTaskStatus.Error
      ) {
        return false;
      }
      return 1500;
    },
    refetchIntervalInBackground: true,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });

  // if the simplify all task is not found, remove the task id from the session storage
  useEffect(() => {
    if (!simplifyAllPollingQuery.isError) {
      return;
    }
    const err = simplifyAllPollingQuery.error;
    if (!(err instanceof Response) || err.status !== 404) {
      return;
    }
    if (documentId && activeCommunication?.id) {
      clearSimplifyAllTaskIdFromSession(documentId, activeCommunication.id);
    }
  }, [
    simplifyAllPollingQuery.isError,
    simplifyAllPollingQuery.error,
    documentId,
    activeCommunication?.id,
  ]);

  const isSimplifyAllWorkInProgress = useMemo(() => {
    const pollStatus = simplifyAllPollingQuery.data?.status;
    const isSimplifyAllPollInFlight =
      !!simplifyAllTaskId &&
      pollStatus !== ConsumerTaskStatus.Complete &&
      pollStatus !== ConsumerTaskStatus.Error;
    return startSimplifyAllMutation.isPending || isSimplifyAllPollInFlight;
  }, [
    startSimplifyAllMutation.isPending,
    simplifyAllTaskId,
    simplifyAllPollingQuery.data?.status,
  ]);

  // if the simplify all task is in progress, set the simplify all saw active work ref to true
  useEffect(() => {
    if (startSimplifyAllMutation.isPending) {
      simplifyAllSawActiveWorkRef.current = true;
    }
    const t = simplifyAllByCommunicationQuery.data;
    if (t != null) {
      if (
        t.status !== ConsumerTaskStatus.Complete &&
        t.status !== ConsumerTaskStatus.Error
      ) {
        simplifyAllSawActiveWorkRef.current = true;
      }
    }
    const pollStatus = simplifyAllPollingQuery.data?.status;
    if (
      pollStatus &&
      pollStatus !== ConsumerTaskStatus.Complete &&
      pollStatus !== ConsumerTaskStatus.Error
    ) {
      simplifyAllSawActiveWorkRef.current = true;
    }
  }, [
    startSimplifyAllMutation.isPending,
    simplifyAllByCommunicationQuery.data,
    simplifyAllPollingQuery.data?.status,
  ]);

  const clientContactLanguageMutation = useMutation({
    mutationFn: async (params: ClientContactLanguageParams) => {
      const client = clientQuery.data;
      if (!client?.firmId || !client?.id) {
        throw new Error("Client is required");
      }
      return clientApi.clientPut({
        firmId: client.firmId,
        id: client.id,
        client: {
          ...client,
          contactLanguage: params.contactLanguage,
        },
      });
    },
    onMutate: async (params) => {
      return { previous: params.previousCommunicationLanguage };
    },
    onSuccess: (_data, params) => {
      lastSyncedContactLanguage.current = params.contactLanguage;
      queryClient.invalidateQueries({
        queryKey: clientGetOptions(
          clientApi,
          user?.firmId,
          documentQuery.data?.clientId ?? "",
        ).queryKey,
      });
      if (
        params.contactLanguage === ContactLanguages.Spanish &&
        primaryTaskCount > 0 &&
        !awaitingCommsAfterBulkSimplifySuccess &&
        !isSimplifyAllWorkInProgress &&
        !batchCreateTasksForAllMutation.isPending &&
        !createDraftClientCommunicationMutation.isPending &&
        !communicationsQuery.isFetching &&
        !communicationsQuery.isLoading
      ) {
        startBulkTranslationMutation.mutate();
      }
    },
    onError: (error, _params, context) => {
      toast.error("Failed to save client language. Please try again.");
      rg4js("send", {
        error: normalizeError(error),
      });
      if (context?.previous !== undefined) {
        setLanguage(context.previous);
      }
    },
  });

  // if the bulk translation task is complete, set the bulk translation complete ref to true and invalidate the communications query
  useEffect(() => {
    if (awaitingCommsAfterBulkSimplifySuccess) {
      return;
    }
    if (
      startBulkTranslationMutation.isSuccess &&
      (translationPollingQuery.data?.status === ConsumerTaskStatus.Complete ||
        translationPollingQuery.data?.status === ConsumerTaskStatus.Error) &&
      !bulkTranslationComplete.current
    ) {
      if (translationPollingQuery.data?.status === ConsumerTaskStatus.Error) {
        toast.error("Failed to translate the task. Please try again.");
        rg4js("send", {
          error: normalizeError(translationPollingQuery.error),
        });
      }
      bulkTranslationComplete.current = true;
      communicationsQuery.refetch();
    }
  }, [
    awaitingCommsAfterBulkSimplifySuccess,
    startBulkTranslationMutation.isSuccess,
    translationPollingQuery.data?.status,
    communicationsQuery,
    translationPollingQuery.error,
  ]);

  // Start the bulk translation mutation if there are any English primaries that need to be translated to Spanish
  useEffect(() => {
    if (language !== CommunicationLanguage.ES) return;
    if (!activeCommunication?.id) return;
    if (isReviewLocked) return;
    if (isSimplifyAllWorkInProgress) return;
    if (batchCreateTasksForAllMutation.isPending) return;
    if (createDraftClientCommunicationMutation.isPending) return;
    if (awaitingCommsAfterBulkSimplifySuccess) return;
    if (bulkTranslationComplete.current) return;
    if (primaryTaskCount === 0) return;
    if (!isBulkSpanishTranslationNeeded) return;
    if (communicationsQuery.isFetching || communicationsQuery.isLoading) {
      return;
    }
    if (startBulkTranslationMutation.isPending) return;
    if (startBulkTranslationMutation.isError) return;

    // ok.. now we can start the bulk translation mutation
    const pollStatus = translationPollingQuery.data?.status;
    const isBulkTranslationTaskInProgress =
      !!startBulkTranslationMutation.data?.id &&
      pollStatus !== ConsumerTaskStatus.Complete &&
      pollStatus !== ConsumerTaskStatus.Error;
    if (isBulkTranslationTaskInProgress) return;
    // Start the bulk translation mutation
    startBulkTranslationMutation.mutate();
  }, [
    language,
    activeCommunication?.id,
    isReviewLocked,
    isSimplifyAllWorkInProgress,
    batchCreateTasksForAllMutation.isPending,
    createDraftClientCommunicationMutation.isPending,
    awaitingCommsAfterBulkSimplifySuccess,
    primaryTaskCount,
    isBulkSpanishTranslationNeeded,
    communicationsQuery.isFetching,
    communicationsQuery.isLoading,
    startBulkTranslationMutation.isPending,
    startBulkTranslationMutation.isError,
    startBulkTranslationMutation.data?.id,
    startBulkTranslationMutation.mutate,
    translationPollingQuery.data?.status,
  ]);

  // if the simplify all task is complete, set the simplify all workflow complete ref to true and send a success or error toast
  // also remove the task id from the session storage, set the simplify all saw active work ref to false, and refetch/invalidat communications
  // useLayoutEffect: set isAwaitingCommsAfterSimplifyAll true before paint so the stale 1-item list is never shown; refetch then clears it
  useLayoutEffect(() => {
    if (simplifyAllWorkflowComplete.current) {
      return;
    }
    const status = simplifyAllPollingQuery.data?.status;
    if (
      status !== ConsumerTaskStatus.Complete &&
      status !== ConsumerTaskStatus.Error
    ) {
      return;
    }
    if (!simplifyAllSawActiveWorkRef.current) {
      simplifyAllWorkflowComplete.current = true;
      return;
    }
    simplifyAllWorkflowComplete.current = true;
    const commsKey = getCommunicationsForObjectOptions(
      communicationApi,
      documentId,
      ObjectTypeEnum.Document,
    ).queryKey;
    if (status === ConsumerTaskStatus.Error) {
      toast.error("Bulk client tasks job failed. Please try again.");
      rg4js("send", {
        error: normalizeError(simplifyAllPollingQuery.error),
      });
      queryClient.invalidateQueries({ queryKey: commsKey });
    } else {
      if (language === CommunicationLanguage.ES) {
        setAwaitingCommsAfterBulkSimplifySuccess(true);
      }
      setIsAwaitingCommsAfterSimplifyAll(true);
      queryClient
        .refetchQueries({ queryKey: commsKey })
        .catch(() => {
          queryClient.invalidateQueries({ queryKey: commsKey });
        })
        .finally(() => {
          setIsAwaitingCommsAfterSimplifyAll(false);
        });
    }
    if (activeCommunication?.id) {
      queryClient.setQueryData(
        communicationGetSimplifyAllTaskByCommunicationOptions(
          communicationApi,
          activeCommunication.id,
        ).queryKey,
        null,
      );
    }
    if (documentId && activeCommunication?.id) {
      clearSimplifyAllTaskIdFromSession(documentId, activeCommunication.id);
    }
  }, [
    simplifyAllPollingQuery.data?.status,
    simplifyAllPollingQuery.error,
    queryClient,
    communicationApi,
    documentId,
    activeCommunication?.id,
    language,
  ]);

  // if the bulk simplify job is successful, set the awaiting comms after bulk simplify success ref to false
  useEffect(() => {
    if (!awaitingCommsAfterBulkSimplifySuccess) {
      return;
    }
    if (communicationsQuery.isFetching || communicationsQuery.isLoading) {
      return;
    }
    if (communicationsQuery.isError) {
      setAwaitingCommsAfterBulkSimplifySuccess(false);
      return;
    }
    if (communicationsQuery.isSuccess) {
      setAwaitingCommsAfterBulkSimplifySuccess(false);
    }
  }, [
    awaitingCommsAfterBulkSimplifySuccess,
    communicationsQuery.isFetching,
    communicationsQuery.isLoading,
    communicationsQuery.isSuccess,
    communicationsQuery.isError,
  ]);

  const reviewTasksPanelWidth =
    language === CommunicationLanguage.ES ? "996px" : "746px";

  const isBulkSimplifyJobBusy =
    isSimplifyAllWorkInProgress ||
    createDraftClientCommunicationMutation.isPending ||
    batchCreateTasksForAllMutation.isPending;

  const isSimplifyAllProgressPanelVisible =
    (isBulkSimplifyJobBusy && bulkSimplifyLoadingSource !== "emptyVerbatim") ||
    isAwaitingCommsAfterSimplifyAll;

  // verbatim batch: no full-page progress; only the trigger button's loading state
  const isReviewTasksMainContentVisible =
    (!isBulkSimplifyJobBusy && !isAwaitingCommsAfterSimplifyAll) ||
    bulkSimplifyLoadingSource === "emptyVerbatim";

  // if the bulk simplify job is not busy, set the bulk simplify loading source to null
  useEffect(() => {
    if (prevIsBulkSimplifyJobBusy.current && !isBulkSimplifyJobBusy) {
      setBulkSimplifyLoadingSource(null);
    }
    prevIsBulkSimplifyJobBusy.current = isBulkSimplifyJobBusy;
  }, [isBulkSimplifyJobBusy]);

  const bulkSimplifyProgressPercent = useMemo(() => {
    if (!isSimplifyAllProgressPanelVisible) {
      return 0;
    }
    const view =
      simplifyAllPollingQuery.data ??
      simplifyAllByCommunicationQuery.data ??
      startSimplifyAllMutation.data;
    const fromApi = view?.progressPercentage;
    if (typeof fromApi === "number" && !Number.isNaN(fromApi)) {
      return Math.min(100, Math.max(0, fromApi));
    }
    const total = view?.totalItems;
    const done = view?.processedCount;
    if (
      typeof total === "number" &&
      total > 0 &&
      typeof done === "number" &&
      !Number.isNaN(done)
    ) {
      return Math.min(100, Math.max(0, (done / total) * 100));
    }
    return 0;
  }, [
    isSimplifyAllProgressPanelVisible,
    simplifyAllPollingQuery.data,
    simplifyAllByCommunicationQuery.data,
    startSimplifyAllMutation.data,
  ]);

  const bulkSimplifyProgressLabel = useMemo(() => {
    if (!isSimplifyAllProgressPanelVisible) {
      return null;
    }
    const view =
      simplifyAllPollingQuery.data ??
      simplifyAllByCommunicationQuery.data ??
      startSimplifyAllMutation.data;
    const total = view?.totalItems;
    const done = view?.processedCount;
    const doneNum = typeof done === "number" && !Number.isNaN(done) ? done : 0;
    if (typeof total === "number" && total > 0) {
      return `${doneNum} of ${total} items`;
    }
    return null;
  }, [
    isSimplifyAllProgressPanelVisible,
    simplifyAllPollingQuery.data,
    startSimplifyAllMutation.data,
    simplifyAllByCommunicationQuery.data,
  ]);

  const isLanguageControlsDisabled =
    isBulkSimplifyJobBusy ||
    clientContactLanguageMutation.isPending ||
    isBulkTranslationWorkInProgress ||
    isAwaitingCommsAfterSimplifyAll;

  const isBulkCreateBlockedWithoutCase =
    !activeCommunication?.id &&
    (!documentQuery.data?.caseId ||
      caseQuery.isLoading ||
      !caseQuery.isSuccess ||
      !caseQuery.data?.shortTitle);

  const isSimplifyAllBulkActionsDisabled =
    isReviewLocked ||
    isBulkSimplifyJobBusy ||
    isBulkCreateBlockedWithoutCase ||
    isVerbatimAllAwaitingTasksOnScreen ||
    isAwaitingCommsAfterSimplifyAll;

  const isNoTasksCreatedYetMessageVisible =
    isEmptyClientTasksState &&
    !isBulkSimplifyJobBusy &&
    !isBulkTranslationWorkInProgress &&
    !isVerbatimAllAwaitingTasksOnScreen &&
    !isAwaitingCommsAfterSimplifyAll;

  const isVerbatimCreateAllStatusVisible =
    isEmptyClientTasksState && isVerbatimAllAwaitingTasksOnScreen;

  async function resolveCommunicationIdForBulkActions(): Promise<
    string | undefined
  > {
    if (activeCommunication?.id) {
      return activeCommunication.id;
    }
    try {
      return await createDraftClientCommunicationMutation.mutateAsync();
    } catch {
      return undefined;
    }
  }

  async function runCreateCommunicationThenBulkSimplify(
    source: Extract<BulkSimplifyActionSource, "emptySimplified" | "remaining">,
  ) {
    if (isReviewLocked) return;
    setBulkSimplifyLoadingSource(source);
    const communicationId = await resolveCommunicationIdForBulkActions();
    if (!communicationId) {
      setBulkSimplifyLoadingSource(null);
      toast.error("Could not create client communication. Try again later.");
      return;
    }
    startSimplifyAllMutation.mutate(communicationId);
  }

  async function runCreateCommunicationThenVerbatimAll() {
    if (isReviewLocked) return;
    setBulkSimplifyLoadingSource("emptyVerbatim");
    setIsVerbatimAllAwaitingTasksOnScreen(true);
    const communicationId = await resolveCommunicationIdForBulkActions();
    if (!communicationId) {
      setBulkSimplifyLoadingSource(null);
      setIsVerbatimAllAwaitingTasksOnScreen(false);
      toast.error("Could not create client communication. Try again later.");
      return;
    }
    batchCreateTasksForAllMutation.mutate(communicationId);
  }

  function onSelectEnglish() {
    if (isLanguageControlsDisabled) return;
    if (language === CommunicationLanguage.EN) return;
    const previous = language;
    setLanguage(CommunicationLanguage.EN);
    clientContactLanguageMutation.mutate({
      contactLanguage: ContactLanguages.EnglishUs,
      previousCommunicationLanguage: previous,
    });
  }

  function onSelectSpanish() {
    if (isLanguageControlsDisabled) return;
    if (language === CommunicationLanguage.ES) return;
    const previous = language;
    setLanguage(CommunicationLanguage.ES);
    clientContactLanguageMutation.mutate({
      contactLanguage: ContactLanguages.Spanish,
      previousCommunicationLanguage: previous,
    });
  }

  return {
    // client language: state, handlers, layout width
    language,
    setLanguage,
    onSelectEnglish,
    onSelectSpanish,
    reviewTasksPanelWidth,

    // bulk simplify + verbatim: progress, busy flags, and entry points
    isSimplifyAllProgressPanelVisible,
    bulkSimplifyProgressPercent,
    bulkSimplifyProgressLabel,
    bulkSimplifyLoadingSource,
    isBulkSimplifyJobBusy,
    isVerbatimAllAwaitingTasksOnScreen,
    runCreateCommunicationThenBulkSimplify,
    runCreateCommunicationThenVerbatimAll,

    // empty / verbatim (non-simplified) copy visibility
    isEmptyClientTasksState,
    isVerbatimCreateAllStatusVisible,
    isNoTasksCreatedYetMessageVisible,
    isSimplifyAllBulkActionsDisabled,
    isReviewTasksMainContentVisible,
    isLanguageControlsDisabled,
    isBulkTranslationWorkInProgress,
  };
}

export type ReviewClientTasksSimplifyAndLanguage = ReturnType<
  typeof useReviewClientTasksSimplifyAndLanguage
>;
